import React, { useMemo } from "react";
import {Card, CardHeader, CardContent, Typography, IconButton, Chip, Box, Skeleton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import QrCodeIcon from "@mui/icons-material/QrCode";
import './Checkpoint.css'
import { capitalizeCamelCase, capitalizeSentence, findDeepValue, flattenObj, formatDate, isFilePath, parseUnderscore } from "../../services/functions";
import FilePreview from "../utils/FilePreview";
import theme, { buttonClasses } from "../utils/theme";
import { Button } from "../utils";
import { DefaultLocation, GateIn, Weighing } from "../utils/Genericstats";
import { usePlantContext } from "../../context/plantContext";

/* Calculate duration between two dates*/
export function calculateDuration(startDate, endDate) {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = Math.abs(end - start);
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0 || hours > 0) formatted += `${minutes}m `;
    formatted += `${seconds}s`;

    return { hours, minutes, seconds, totalSeconds, formatted: formatted.trim() };
}

/*Format total seconds into hours, minutes, seconds object*/
function formatTotalSeconds(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0 || hours > 0) formatted += `${minutes}m `;
    formatted += `${seconds}s`;

    return { hours, minutes, seconds, totalSeconds, formatted: formatted.trim() };
}

/* Get checkpoint icon based on checkpoint type or name */
function getCheckpointIcon(displayName, checkpointType) {
    const name = displayName?.toLowerCase() || "";
    const type = checkpointType?.toLowerCase() || "";

    if (name.includes("gate in") || name.includes("entry")) {
        return <GateIn />;
    }
    if (name.includes("gate out") || name.includes("exit")) {
        return <GateIn />;
    }
    if (name.includes("weigh") || name.includes("wb")) {
        return <Weighing sx={{ fontSize: 20 }} />;
    }
    if (type === "bay" || name.includes("dock") || name.includes("bay")) {
        return <LocalShippingIcon sx={{ fontSize: 20 }} />;
    }
    if (type === "access") {
        return <AccessTimeIcon sx={{ fontSize: 20 }} />;
    }
    return <DefaultLocation />;
}

/* Get status chip color*/
function getStatusColor(status) {
    const s = status?.toLowerCase() || "";
    if (s === "completed" || s === "complete") return { bg: "#e8f5e9", color: "#2e7d32" };
    if (s === "ongoing" || s === "in progress") return { bg: "#fff3e0", color: "#ef6c00" };
    if (s === "pending") return { bg: "#fce4ec", color: "#c62828" };
    return { bg: "#e3f2fd", color: "#1565c0" };
}

/* Single checkpoint card in the timeline*/
function CheckpointCard({
    checkpoint,
    isExpanded = true,
    checkpointColor = "#ef6c00",
    durationAtCheckpoint = null,
}) {
    const { displayName, inTime, outTime, checkpointType, formData, isOfflinePacket, entryType } = checkpoint;
    //  ADDING THIS: Only show duration if it's greater than 0 seconds
    const shouldShowDuration = durationAtCheckpoint && durationAtCheckpoint.totalSeconds > 0;
    return (
        <Box sx={{
            backgroundColor: "#fff",
            border: `1px solid ${checkpointColor}20`,
            borderLeft: `4px solid ${checkpointColor}`,
            borderRadius: "8px",
            p: 1.5,
            position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}>
            {/* Header with name and icons */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: checkpointColor,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}>
                    {displayName}
                    {isOfflinePacket && (
                        <Chip
                            label="Offline"
                            size="small"
                            sx={{
                                fontSize: "10px",
                                height: "18px",
                                backgroundColor: "#fff3e0",
                                color: "#ef6c00",
                            }}
                        />
                    )}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {/* Duration at checkpoint badge  IT will be visible on ui only when duration is greater than 0 sec*/}
                    {durationAtCheckpoint && shouldShowDuration  && (
                        <Chip
                            label={durationAtCheckpoint.formatted}
                            size="small"
                            sx={{
                                fontSize: "10px",
                                height: "20px",
                                backgroundColor: `${checkpointColor}15`,
                                color: checkpointColor,
                                fontWeight: 600,
                            }}
                        />
                    )}
                    {entryType === "camera" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <CameraAltIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                    {entryType === "scan" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <SettingsInputAntennaIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                    {entryType === "manual" && (
                        <IconButton size="small" sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Time rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {inTime && (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            {checkpoint.inTimeLabel || (checkpointType === "access" ? "Arrived" : checkpointType === "bay" ? "Arrived" : "In Time")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#333", fontWeight: 500 }}>
                            {formatDate(inTime)}
                        </Typography>
                    </Box>
                )}
                {outTime && (
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                            {checkpoint.outTimeLabel || (checkpointType === "access" ? "Exit" : checkpointType === "bay" ? "Exit" : "Out Time")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#333", fontWeight: 500 }}>
                            {formatDate(outTime)}
                        </Typography>
                    </Box>
                )}
            </Box>

            {checkpoint.blocks?.map((block, i) => (
                <Box key={i} sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#333",
                            mb: 1,
                            textAlign: "center",
                        }}
                    >
                        {capitalizeSentence(block.sectionType)}
                    </Typography>

                    {block.rows.map((row, j) => {
                        let value = row.value;

                        if (
                            value === null ||
                            value === undefined ||
                            value === "" ||
                            value === "-" ||
                            (Array.isArray(value) && value.length === 0)
                        ) {
                            return null;
                        }

                        const isoPattern =
                            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
                        if (typeof value === "string" && isoPattern.test(value)) {
                            value = formatDate(value);
                        }

                        return (
                            <Box
                                key={j}
                                sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
                            >
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    {row.label}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: "#333",
                                        fontWeight: 500,
                                        maxWidth: "60%",
                                        textAlign: "right",
                                    }}
                                >
                                    {Array.isArray(value) ? (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
                                            {value.map((el, k) =>
                                                isFilePath(el) ? (
                                                    <FilePreview
                                                        key={k}
                                                        fileKey={el}
                                                        extension={String(el).split(".").pop()}
                                                    />
                                                ) : (
                                                    <span key={k}>{capitalizeCamelCase(String(el))}</span>
                                                )
                                            )}
                                        </Box>
                                    ) : isFilePath(value) ? (
                                        <FilePreview
                                            fileKey={value}
                                            extension={String(value).split(".").pop()}
                                        />
                                    ) : (
                                        capitalizeCamelCase(String(value))
                                    )}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}

/* Journey Breakdown component showing time between checkpoints */
function JourneyBreakdown({ breakdownItems, isOngoing = false }) {
    const filteredItems = breakdownItems?.filter(item => {
        const duration = item.duration || "";
        return duration !== "0s" && duration !== "";
    }) || [];

    // ✅ CHANGE THIS: Check filteredItems instead of breakdownItems
    if (filteredItems.length === 0) return null;
    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--primary-color)",
                borderLeft: "4px solid var(--primary-color)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "var(--primary-color)",
                    letterSpacing: "0.5px",
                    mb: 1.5,
                }}
            >
                Journey Breakdown
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 1,
                }}
            >
                {filteredItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: "1 1 auto",
                            width: "auto",
                            backgroundColor: item.isLive ? "#e8f5e9" : "#fff",
                            borderRadius: "8px",
                            p: 1,
                            textAlign: "center",
                            border: item.isLive ? "1px solid #4caf50" : "1px solid var(--primary-color)",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "10px",
                                color: "#666",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                            }}
                        >
                            {item.label}
                            {item.isLive && (
                                <Box
                                    component="span"
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        backgroundColor: "#4caf50",
                                        animation: "pulse 1.5s infinite",
                                        "@keyframes pulse": {
                                            "0%": { opacity: 1 },
                                            "50%": { opacity: 0.4 },
                                            "100%": { opacity: 1 },
                                        },
                                    }}
                                />
                            )}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 700,
                                mt: 0.5,
                                color: item.isLive ? "#4caf50" : "var(--primary-color)",
                            }}
                        >
                            {item.duration}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * ============================================
 * TIMESTAMP-BASED DURATION CALCULATION
 * ============================================
 * 
 * This approach fixes three critical bugs:
 * 
 * 1. Missing outTime bug: If checkpoint has inTime but no outTime,
 *    time until next event is correctly attributed as "At [checkpoint]"
 *    not as transit time.
 * 
 * 2. Out-of-order packets: All timestamps are sorted chronologically,
 *    so late-arriving offline packets are handled correctly.
 * 
 * 3. Ongoing trip gap: For ongoing trips, we calculate from the last
 *    timestamp to current time, ensuring breakdown + live = total.
 * 
 * Model: timestamp → timestamp (not checkpoint → checkpoint)
 */
function calculateBreakdownFromTimestamps(checkpoints, tripStatus, currentTime = null) {
    // 1. Collect ALL timestamps with context
    const timestamps = [];
    
    checkpoints.forEach((cp, cpIndex) => {
        if (cp.originalInTime) {
            timestamps.push({
                time: cp.originalInTime,
                type: 'in',
                checkpointName: cp.displayName,
                checkpointIndex: cpIndex,
            });
        }
        if (cp.originalOutTime) {
            timestamps.push({
                time: cp.originalOutTime,
                type: 'out',
                checkpointName: cp.displayName,
                checkpointIndex: cpIndex,
            });
        }
    });

    // 2. Sort chronologically (handles out-of-order packets)
    timestamps.sort((a, b) => new Date(a.time) - new Date(b.time));

    // 3. For ongoing trips, add current time as final timestamp
    if (tripStatus === "ongoing" && currentTime && timestamps.length > 0) {
        const lastTimestamp = timestamps[timestamps.length - 1];
        timestamps.push({
            time: currentTime,
            type: 'now',
            checkpointName: lastTimestamp.checkpointName,
            checkpointIndex: lastTimestamp.checkpointIndex,
            isLive: true,
        });
    }

    // 4. Calculate durations between consecutive timestamps
    const breakdownItems = [];
    const durationsByCheckpoint = {}; // To store "at checkpoint" durations for card display

    for (let i = 0; i < timestamps.length - 1; i++) {
        const current = timestamps[i];
        const next = timestamps[i + 1];

        const duration = calculateDuration(current.time, next.time);
        if (!duration || duration.totalSeconds === 0) continue;

        // Determine the semantic meaning of this time segment
        let label;
        let segmentType;
        let isLive = next.isLive || false;

        if (current.checkpointIndex === next.checkpointIndex) {
            if (current.type === 'out' && next.type === 'now') {
                // Left current checkpoint, hasn't started at next checkpoint, still ongoing
                label = `Since ${current.checkpointName}`;
                segmentType = 'after';
            } else{
                // Same checkpoint: time spent AT this checkpoint
                // Covers: in→out, in→now (ongoing at checkpoint)
                label = `At ${current.checkpointName}`;
                segmentType = 'at';
            }
            
            // Accumulate duration at this checkpoint
            if (!durationsByCheckpoint[current.checkpointIndex]) {
                durationsByCheckpoint[current.checkpointIndex] = 0;
            }
            durationsByCheckpoint[current.checkpointIndex] += duration.totalSeconds;
        } else {
            // Different checkpoints
            if (current.type === 'out' && next.type === 'in') {
                // Normal transit: left checkpoint A, arrived at checkpoint B
                label = `${current.checkpointName} → ${next.checkpointName}`;
                segmentType = 'transit';
            } else if (current.type === 'in' && next.type === 'in') {
                // Missing outTime: still AT current checkpoint until next checkpoint's inTime
                // This is the KEY FIX for Bug #1
                label = `At ${current.checkpointName}`;
                segmentType = 'at';
                
                if (!durationsByCheckpoint[current.checkpointIndex]) {
                    durationsByCheckpoint[current.checkpointIndex] = 0;
                }
                durationsByCheckpoint[current.checkpointIndex] += duration.totalSeconds;
            } else if (current.type === 'out' && next.type === 'out') {
                // Edge case: two consecutive outs (rare, could be data issue)
                label = `${current.checkpointName} → ${next.checkpointName}`;
                segmentType = 'transit';
            } else if (current.type === 'in' && next.type === 'out' && current.checkpointIndex !== next.checkpointIndex) {
                // in at A → out at B (overlapping/data anomaly)
                label = `${current.checkpointName} → ${next.checkpointName}`;
                segmentType = 'transit';
            } else if (current.type === 'out' && next.type === 'now') {
                // Left last checkpoint, now in transit/waiting (ongoing trip)
                label = `After ${current.checkpointName}`;
                segmentType = 'after';
                isLive = true;
            } else if (current.type === 'in' && next.type === 'now') {
                // Still at checkpoint (ongoing trip) - same or different index
                label = `At ${current.checkpointName}`;
                segmentType = 'at';
                isLive = true;
                
                if (!durationsByCheckpoint[current.checkpointIndex]) {
                    durationsByCheckpoint[current.checkpointIndex] = 0;
                }
                durationsByCheckpoint[current.checkpointIndex] += duration.totalSeconds;
            } else {
                // Fallback for any other edge cases
                label = `${current.checkpointName} → ${next.checkpointName}`;
                segmentType = 'unknown';
            }
        }

        breakdownItems.push({
            label,
            duration: duration.formatted,
            totalSeconds: duration.totalSeconds,
            segmentType,
            isLive,
            fromCheckpoint: current.checkpointName,
            toCheckpoint: next.checkpointName,
        });
    }

    // 5. Convert accumulated checkpoint durations to objects for card display
    const checkpointDurations = {};
    for (const [index, seconds] of Object.entries(durationsByCheckpoint)) {
        checkpointDurations[index] = formatTotalSeconds(seconds);
    }

    return { breakdownItems, checkpointDurations, timestamps };
}

/**
 * Process checkpoint data for timeline display
 * Uses timestamp-based model for accurate duration calculation
 */
export function processCheckpointsForTimeline(data, historicalDataHeaderMap = {}, inTimeOutTimeConfig = {}) {
    if (!data || !data.statusHistory) return { 
        checkpoints: [], 
        breakdownItems: [], 
        totalDuration: null, 
        durations: [],
        isLiveDuration: false,
        timestamps: [],
    };

    const history = [...data.statusHistory];
    const eventData = data.data ? [...data.data] : [];
    const checkpoints = [];
    const tripStatus = data?.trip_status?.toLowerCase() || "";
    
    // Process each checkpoint
    history.forEach((event, index) => {
        const { displayName, outTime, inTime, checkpointType, checkpointId, _basis, type } = event;
        const isOfflinePacket = _basis === "offline-packet";
        const { hideInTime = false, hideOutTime = false, inTimeLabel = "In Time", outTimeLabel = "Out Time" } = inTimeOutTimeConfig[checkpointId] || {};

        let blocks = [];

        while (
            eventData.length &&
            (
                eventData[0]._id === event.checkInDataID ||
                eventData[0]._id === event.checkOutDataID
            )
        ) {
            let currentData = eventData.shift();
            let type = currentData.type;

            let rows = [];
            const currentTableHeader = historicalDataHeaderMap[`${currentData.checkpointId}-${type}`];

            if (currentTableHeader) {
                currentTableHeader.forEach((header) => {
                    let value = findDeepValue(currentData, header.name?.split("."));
                    if (value == "-") value = findDeepValue(currentData.formData, header.name?.split("."));
                    if (value) {
                        rows.push({
                            label: header.label,
                            value
                        });
                    }
                });
            } else {
                let processedData = {};
                flattenObj(processedData, currentData.formData);
                delete processedData.vehicleId;

                for (let key in processedData) {
                    if (processedData[key]) {
                        rows.push({
                            label: capitalizeSentence(capitalizeCamelCase(parseUnderscore(key))),
                            value: processedData[key]
                        });
                    }
                }
            }

            const cleanedRows = rows.filter(r => {
                const v = r.value;
                return !(
                    v === null ||
                    v === undefined ||
                    v === "" ||
                    v === "-" ||
                    (Array.isArray(v) && v.length === 0)
                );
            });

            if (cleanedRows.length) {
                blocks.push({
                    sectionType: type,
                    rows: cleanedRows
                });
            }
        }

        checkpoints.push({
            displayName,
            // UI display times (respects hide config)
            inTime: hideInTime ? null : inTime,
            outTime: hideOutTime ? null : outTime,
            // ORIGINAL times for calculation (ignores hide config)
            originalInTime: inTime,
            originalOutTime: outTime,
            inTimeLabel,
            outTimeLabel,
            checkpointType,
            checkpointId,
            isOfflinePacket,
            formData: {},
            formDataType: "",
            originalIndex: index,
            entryType: type,
            blocks
        });
    });

    // Calculate breakdown using timestamp-based model
    // For initial render, don't include live time (that's handled in component)
    const { breakdownItems, checkpointDurations, timestamps } = 
        calculateBreakdownFromTimestamps(checkpoints, tripStatus === "ongoing" ? "completed" : tripStatus);

    // Attach duration at each checkpoint for card display
    checkpoints.forEach((cp, index) => {
        cp.durationAtCheckpoint = checkpointDurations[index] || null;
    });

    // Calculate transit durations between consecutive checkpoints for timeline connector
    // We look through sorted timestamps to find out→in transitions
    const sortedTimestamps = [...timestamps].sort((a, b) => new Date(a.time) - new Date(b.time));
    
    for (let i = 0; i < sortedTimestamps.length - 1; i++) {
        const current = sortedTimestamps[i];
        const next = sortedTimestamps[i + 1];
        
        // Only show transit time on timeline connector when:
        // current is 'out' and next is 'in' at different checkpoints
        if (current.type === 'out' && next.type === 'in' && current.checkpointIndex !== next.checkpointIndex) {
            const transitDuration = calculateDuration(current.time, next.time);
            if (transitDuration && transitDuration.totalSeconds > 0) {
                // Find the checkpoint and attach durationToNext
                const cpIndex = current.checkpointIndex;
                if (checkpoints[cpIndex]) {
                    checkpoints[cpIndex].durationToNext = transitDuration;
                }
            }
        }
    }

    // Calculate total duration
    let directTotalDuration = null;
    if (sortedTimestamps.length > 0) {
        const firstTimestamp = sortedTimestamps[0];
        const lastTimestamp = sortedTimestamps[sortedTimestamps.length - 1];
        
        if (tripStatus === "ongoing") {
            // Ongoing: first timestamp → now (calculated in component for live update)
            directTotalDuration = calculateDuration(firstTimestamp.time, new Date().toISOString());
        } else if (tripStatus === "completed") {
            // Completed: first timestamp → last timestamp
            directTotalDuration = calculateDuration(firstTimestamp.time, lastTimestamp.time);
        }
        // Terminated/Cancelled: stays null
    }

    return {
        checkpoints,
        breakdownItems,
        totalDuration: directTotalDuration,
        isLiveDuration: tripStatus === "ongoing",
        timestamps: sortedTimestamps,
    };
}

/*MAIN VEHICLE JOUNEY DETAILED COMPONENT */
function VehicleJourneyDetailCard({
    isOpen,
    onClose,
    data,
    tableHeader = [],
    historicalDataHeaderMap = {},
    inTimeOutTimeConfig = {},
    handlePrint,
    customizeTripWorkflow = false,
    handleCustomizeTripWorkflow,
    printLabelPerm = false,
    height = "75vh",
    handleVehicleNumberEdit,
    isVehicleNumberEditable = false,
}) {
    // State for live duration and breakdown
    const [liveDuration, setLiveDuration] = React.useState(null);
    const [liveBreakdownItems, setLiveBreakdownItems] = React.useState([]);

    // Process data for display
    const { checkpoints, breakdownItems, totalDuration, timestamps } = useMemo(() =>
        processCheckpointsForTimeline(data, historicalDataHeaderMap, inTimeOutTimeConfig),
        [data, historicalDataHeaderMap, inTimeOutTimeConfig]
    );
    const { plantOptions } = usePlantContext();
    // console.log("plantOptions", plantOptions)
    // Check if trip is ongoing
    const isOngoing = data?.trip_status?.toLowerCase() === "ongoing";

    // Get the trip start time (first timestamp)
    const tripStartTime = useMemo(() => {
        if (timestamps && timestamps.length > 0) {
            return timestamps[0].time;
        }
        return null;
    }, [timestamps]);

    // Effect to calculate live duration and breakdown every second
    React.useEffect(() => {
        if (!isOngoing || !tripStartTime || checkpoints.length === 0) {
            setLiveDuration(null);
            setLiveBreakdownItems(breakdownItems);
            return;
        }

        const calculateLive = () => {
            const now = new Date();
            const nowISO = now.toISOString();

            // Calculate total duration
            const duration = calculateDuration(tripStartTime, nowISO);
            setLiveDuration(duration);

            // Recalculate breakdown with current time included
            const { breakdownItems: liveItems } = calculateBreakdownFromTimestamps(
                checkpoints, 
                "ongoing", 
                nowISO
            );
            setLiveBreakdownItems(liveItems);
        };

        calculateLive(); // Initial calculation

        // Update every second
        const intervalId = setInterval(calculateLive, 1000);

        return () => clearInterval(intervalId);
    }, [isOngoing, tripStartTime, checkpoints, breakdownItems]);

    // Use live values if ongoing, otherwise use static values
    const displayDuration = isOngoing ? (liveDuration || totalDuration) : totalDuration;
    const displayBreakdownItems = isOngoing ? liveBreakdownItems : breakdownItems;

    // Extract header details from tableHeader
    const headerDetails = useMemo(() => {
        if (!data || !tableHeader) return [];
        return tableHeader.map(header => ({
            label: header.label,
            name: header.name,
            value: header.type === "date"
                ? formatDate(findDeepValue(data, header.name.split(".")))
                : findDeepValue(data, header.name.split("."))
        }));
    }, [data, tableHeader]);

    // Get vehicle number and trip info
    const vehicleNumber = data?.vehicleNumber || "N/A";
    const tripId = data?.trip_counter || "";
    const warehouse = data?.plantName || "";
    const tripStatus = data?.trip_status || "-";
    const remarks = data?.remarks || "-";
    const statusColors = getStatusColor(tripStatus);
    if (!isOpen) return null;

    return (
        <div className={`detail_card ${isOpen ? "open" : ""}`} >
            <Card sx={{
                borderRadius: "12px",
                border: `1px solid ${theme?.themeOrange || "#ef6c00"}`,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
                {/* Header */}
                <CardHeader
                    sx={{
                        height: "auto",
                        minHeight: "50px",
                        backgroundColor: theme?.themeOrange || "#ef6c00",
                        py: 1.5,
                        px: 2,
                    }}
                    title={
                        <Typography sx={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: 600,
                        }}>
                            Journey Detail - {vehicleNumber}
                        </Typography>
                    }
                    action={
                        <div style={{ display: "flex", gap: "4px" }}>
                            {isVehicleNumberEditable && (
                                <IconButton size="small" onClick={() => handleVehicleNumberEdit(data)} sx={{ color: "white" }}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    }
                />

                <CardContent sx={{
                    height,
                    overflowY: "auto",
                    overflowX: "hidden",
                    backgroundColor: "#f8f9fa",
                    p: 2,
                }}>
                    {/* Vehicle Info Section */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                        p: 1.5,
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                        border: "1px solid var(--primary-color)",
                        borderLeft: `4px solid var(--primary-color)`,

                    }}>
                        <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        }}>
                            <LocalShippingIcon sx={{ color: "var(--primary-color)", fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                                    {vehicleNumber}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    {
                                        plantOptions?.length > 1 && (
                                            <>{warehouse}</>
                                        )  
                                    }
                                </Typography>
                            </Box>
                            <Chip
                                label={capitalizeCamelCase(tripStatus)}
                                size="small"
                                sx={{
                                    backgroundColor: statusColors.bg,
                                    color: statusColors.color,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: "22px",
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Trip ID and Remarks */}
                    {(remarks !== "-") && (
                        <Box sx={{ mb: 2, px: 0.5 }}>

                            {remarks && remarks !== "-" && (
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    Remarks: {remarks}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Action Buttons */}
                    {(printLabelPerm || customizeTripWorkflow) && (
                        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                            {printLabelPerm && (
                                <Button
                                    onClick={handlePrint}
                                    style={{ ...buttonClasses.lynkitOrangeEmpty }}
                                >
                                    <QrCodeIcon />Print Label
                                </Button>
                            )}
                            {customizeTripWorkflow && (
                                <Button
                                    onClick={handleCustomizeTripWorkflow}
                                    style={{ ...buttonClasses.lynkitOrangeEmpty }}
                                >
                                    Customize Trip Workflow
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* Total Journey Summary */}
                    <Box sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "12px",
                        p: 2,
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid var(--primary-color)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                        borderLeft: "4px solid var(--primary-color)",
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: "11px", color: "var(--primary-color)" }}>
                                {tripId && (
                                    <>
                                        <Typography sx={{ fontSize: "12px", color: "#000" }}>
                                            Trip ID:
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: "20px",
                                            fontWeight: 700,
                                            color: "var(--primary-color)",
                                            display: "flex",
                                            alignItems: "baseline",
                                            gap: 0.5,
                                        }}>
                                            {tripId}
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        </Box>
                        {displayDuration && (
                            <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontSize: "11px", color: "#000", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                    Total Journey Time
                                    {isOngoing && (
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor: "#4caf50",
                                                animation: "pulse 1.5s infinite",
                                                "@keyframes pulse": {
                                                    "0%": { opacity: 1 },
                                                    "50%": { opacity: 0.4 },
                                                    "100%": { opacity: 1 },
                                                },
                                            }}
                                        />
                                    )}
                                </Typography>
                                <Typography sx={{
                                    fontSize: "20px",
                                    fontWeight: 700,
                                    color: isOngoing ? "#4caf50" : "var(--primary-color)",
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 0.5,
                                }}>
                                    {displayDuration.hours > 0 && <><span>{displayDuration.hours}h</span></>}
                                    {displayDuration.minutes > 0 && <><span>{displayDuration.minutes}m</span></>}
                                    <span>{displayDuration.seconds}s</span>
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {(() => {
                        const filteredDetails = headerDetails
                            .filter(item => {
                                return ![
                                    "vehicleNumber",
                                    "trip_counter",
                                    "trip_status",
                                    "plantName",
                                ].includes(item.name);
                            })
                            .filter(item => item.value && item.value !== "-");

                        if (!filteredDetails.length) return null;

                        return (
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    borderRadius: "8px",
                                    p: 1.5,
                                    mb: 2,
                                    border: "1px solid #e0e0e0",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                }}
                            >
                                {filteredDetails.map((item, i) => (
                                    <Box
                                        key={i}
                                        sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, gap:"10px" }}
                                    >
                                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                            {item.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        );
                    })()}

                    {/* Asset Details (if present) */}
                    {data?.mapCount !== undefined && data?.scanCount !== undefined && (
                        <Box sx={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            p: 2,
                            mb: 2,
                            border: "1px solid #e0e0e0",
                        }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 1.5, textAlign: "center" }}>
                                Asset Details
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Mapped</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{data.mapCount}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Scanned</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{data.scanCount}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>Assets Remaining</Typography>
                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: data.mapCount - data.scanCount > 0 ? "#c62828" : "#2e7d32" }}>
                                    {data.mapCount - data.scanCount}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Timeline */}
                    <Box sx={{ position: "relative" }}>
                        {checkpoints.map((checkpoint, index) => {
                            const checkpointColors = [
                                "#ff7700", // orange
                                "#1976d2", // blue
                                "#7b1fa2", // purple
                                "#c62828", // red
                            ];
                            const name = checkpoint.displayName?.toLowerCase() || "";
                            const isGate = name.includes("gate out");

                            const color = isGate ? "#2e7d32" : checkpointColors[index % checkpointColors.length];

                            return (
                                <Box key={index} sx={{ display: "flex", gap: 1.5, mb: index < checkpoints.length - 1 ? 0 : 0 }}>
                                    {/* Timeline indicator */}
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        minWidth: "40px",
                                    }}>
                                        {/* Icon circle */}
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            backgroundColor: color,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            flexShrink: 0,
                                        }}>
                                            {getCheckpointIcon(checkpoint.displayName, checkpoint.checkpointType)}
                                        </Box>
                                        {/* Connector line with duration TO NEXT checkpoint */}
                                        {index < checkpoints.length - 1 && (
                                            <Box sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                minHeight: "60px",
                                                position: "relative",
                                            }}>
                                                <Box sx={{
                                                    width: "2px",
                                                    flex: 1,
                                                    backgroundColor: "#e0e0e0",
                                                }} />
                                                {/* Duration BETWEEN checkpoints (transit time only) */}
                                                {checkpoint.durationToNext && (
                                                    <Typography sx={{
                                                        position: "absolute",
                                                        left: "50%",
                                                        top: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        fontSize: "10px",
                                                        color: "#ef6c00",
                                                        fontWeight: 600,
                                                        backgroundColor: "#fff",
                                                        px: 0.5,
                                                        whiteSpace: "nowrap",
                                                        border: "1px solid #ef6c00",
                                                        borderRadius: "4px",
                                                    }}>
                                                        {checkpoint.durationToNext.formatted}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Checkpoint card */}
                                    <Box sx={{ flex: 1, pb: index < checkpoints.length - 1 ? 2 : 0 }}>
                                        <CheckpointCard
                                            checkpoint={checkpoint}
                                            checkpointColor={color}
                                            durationAtCheckpoint={checkpoint.durationAtCheckpoint}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Journey Breakdown */}
                    <JourneyBreakdown 
                        breakdownItems={displayBreakdownItems} 
                        isOngoing={isOngoing}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

export default VehicleJourneyDetailCard;

// SHOWING ONLY THE SKELETION INSTEAD OF LOADER
function VehicleInfoSkeleton() {
    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            p: 1.5,
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            border: "1px solid var(--primary-color)",
            borderLeft: "4px solid var(--primary-color)",
        }}>
            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: "8px" }} />
            <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Skeleton variant="text" width={120} height={20} />
                    <Skeleton variant="text" width={100} height={16} />
                </Box>
                <Skeleton variant="rounded" width={80} height={22} />
            </Box>
        </Box>
    );
}

function TotalJourneySkeleton() {
    return (
        <Box sx={{
            backgroundColor: "#FFF",
            borderRadius: "12px",
            p: 2,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "1px solid var(--primary-color)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            borderLeft: "4px solid var(--primary-color)",
        }}>
            <Box>
                <Skeleton variant="text" width={60} height={14} />
                <Skeleton variant="text" width={100} height={28} sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ textAlign: "right" }}>
                <Skeleton variant="text" width={120} height={14} />
                <Skeleton variant="text" width={80} height={28} sx={{ mt: 0.5 }} />
            </Box>
        </Box>
    );
}

function CheckpointCardSkeleton({ color }) {
    return (
        <Box sx={{
            backgroundColor: "#fff",
            border: `1px solid ${color}20`,
            borderLeft: `4px solid ${color}`,
            borderRadius: "8px",
            p: 1.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
            }}>
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="rounded" width={60} height={20} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={120} height={16} />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={120} height={16} />
                </Box>
            </Box>
        </Box>
    );
}

function TimelineSkeletons() {
    const checkpointColors = ["#ff7700", "#1976d2", "#388e3c", "#7b1fa2"];

    return (
        <Box sx={{ position: "relative" }}>
            {checkpointColors.map((color, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1.5, mb: index < checkpointColors.length - 1 ? 2 : 0 }}>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minWidth: "40px",
                    }}>
                        <Skeleton
                            variant="circular"
                            width={36}
                            height={36}
                            sx={{ backgroundColor: `${color}40` }}
                        />
                        {index < checkpointColors.length - 1 && (
                            <Box sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minHeight: "60px",
                            }}>
                                <Box sx={{
                                    width: "2px",
                                    flex: 1,
                                    backgroundColor: "#e0e0e0",
                                    minHeight: "60px",
                                }} />
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, pb: index < checkpointColors.length - 1 ? 2 : 0 }}>
                        <CheckpointCardSkeleton color={color} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export function VehicleJourneyDetailSkeleton({ isOpen, onClose, height = "75vh" }) {
    if (!isOpen) return null;

    return (
        <div className="detail_card open">
            <Card sx={{
                borderRadius: "12px",
                border: `1px solid ${theme?.themeOrange || "#ef6c00"}`,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
                <CardHeader
                    sx={{
                        height: "auto",
                        minHeight: "50px",
                        backgroundColor: theme?.themeOrange || "#ef6c00",
                        py: 1.5,
                        px: 2,
                    }}
                    title={
                        <Typography sx={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: 600,
                        }}>
                            Journey Detail - Loading...
                        </Typography>
                    }
                    action={
                        <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    }
                />

                <CardContent sx={{
                    height,
                    overflowY: "auto",
                    overflowX: "hidden",
                    backgroundColor: "#f8f9fa",
                    p: 2,
                }}>
                    <VehicleInfoSkeleton />
                    <TotalJourneySkeleton />
                    <TimelineSkeletons />
                </CardContent>
            </Card>
        </div>
    );
}